import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Input } from 'components/ui/input'
import { Textarea } from 'components/ui/textarea'
import { Select } from 'components/ui/select'
import { Label } from 'components/ui/label'
import { Switch } from 'components/ui/switch'
import { useForm } from 'components/controls/useForm'
import { fetchLookupLists } from 'slices/lookupListSlice'

const PortfolioEditPopup = ({ editPortfolio, recordForEdit }) => {
	const dispatch = useDispatch()
	const { entities: lookupEntities, ids: lookupIds, isInitialized: lookupLoaded } = useSelector(state => state.lookupLists)

	useEffect(() => {
		if (!lookupLoaded) dispatch(fetchLookupLists(undefined))
	}, [dispatch, lookupLoaded])

	const portfolioTypeOptions = useMemo(
		() =>
			lookupIds
				.map(id => lookupEntities[id])
				.filter(item => item && item.listType === 'portfolio')
				.map(item => item.option)
				.sort((a, b) => a.localeCompare(b)),
		[lookupEntities, lookupIds],
	)
	const initialValues = { ...recordForEdit }

	const validate = () => {
		let temp = { ...errors }
		temp.assetName = values.assetName ? '' : 'This field is required.'
		temp.portfolioType = values.portfolioType ? '' : 'This field is required.'
		setErrors({ ...temp })
		return Object.values(temp).every(x => x === '')
	}

	const { values, setValues, errors, setErrors, handleInputChange } = useForm(
		initialValues,
		true,
		validate,
	)

	const submitHandler = e => {
		e.preventDefault()
		if (validate()) {
			editPortfolio(values)
		}
	}

	useEffect(() => {
		if (recordForEdit != null) setValues({ ...recordForEdit })
	}, [recordForEdit, setValues])

	return (
		<form onSubmit={submitHandler} className="space-y-4">
			{/* Portfolio Name */}
			<div>
				<Label htmlFor="assetName" className="block text-xs font-medium mb-1.5 text-gray-500">
					Portfolio Name
				</Label>
				<Input
					id="assetName"
					name="assetName"
					value={values.assetName || ''}
					onChange={handleInputChange}
				/>
				{errors.assetName && (
					<p className="text-xs text-red-500 mt-1">{errors.assetName}</p>
				)}
			</div>

			{/* Portfolio Type */}
			<div>
				<Label htmlFor="portfolioType" className="block text-xs font-medium mb-1.5 text-gray-500">
					Portfolio Type
				</Label>
				<Select
					id="portfolioType"
					name="portfolioType"
					value={values.portfolioType || ''}
					onChange={handleInputChange}
				>
					<option value="">Select type...</option>
					{portfolioTypeOptions.map(type => (
						<option key={type} value={type}>{type}</option>
					))}
				</Select>
				{errors.portfolioType && (
					<p className="text-xs text-red-500 mt-1">{errors.portfolioType}</p>
				)}
			</div>

			{/* Notes */}
			<div>
				<Label htmlFor="description" className="block text-xs font-medium mb-1.5 text-gray-500">
					Notes
				</Label>
				<Textarea
					id="description"
					name="description"
					value={values.description || ''}
					onChange={handleInputChange}
					className="min-h-[80px]"
				/>
			</div>

			{/* Active / Archived Switch */}
			<div className="flex items-start gap-3 py-2">
				<Switch
					name="isActive"
					checked={!!values.isActive}
					onChange={e => handleInputChange({ target: { name: 'isActive', value: e.target.checked } })}
				/>
				<span className="text-sm text-gray-700 leading-snug">
					{values.isActive
						? 'Active: This portfolio is Active and will appear in your Portfolios list.'
						: 'Archived: This portfolio has been archived and will not appear in your Portfolios list.'}
				</span>
			</div>

			{/* Submit */}
			<div className="flex justify-center pt-4">
				<button
					type="submit"
					className="rounded-lg bg-[#0080fe] px-6 py-2 text-sm font-medium text-white hover:bg-[#0070e0] transition-colors"
				>
					Update
				</button>
			</div>
		</form>
	)
}

export default PortfolioEditPopup
